<script setup lang="ts">
import { ref, watch, toRefs } from 'vue';
import useLocale from '../../composables/useLocale';

type Emits = {
  (e: 'save', name: string, comment: string): void;
  (e: 'remove'): void;
  (e: 'cancel'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  name?: string;
  comment?: string;
  state: 'edit' | 'new';
  isSaving?: boolean;
  isRemoving?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  name: '',
  comment: '',
});
const { name, comment } = toRefs(props);

const { t } = useLocale();

const newName = ref(props.name);
const newComment = ref(props.comment);
const isValid = ref(false);
const form = ref();

const requiredRule = (v: string) => !!v || t('bookmark_form_rule');

watch(
  [name, comment],
  ([nameValue, commentValue]) => {
    newName.value = nameValue || '';
    newComment.value = commentValue || '';
  },
  { immediate: true },
);

const submit = async () => {
  const result = await form.value?.validate();
  if (!result?.valid) return;

  emit('save', newName.value.trim(), newComment.value.trim());
};

const handleCancel = () => {
  // Reset form to original values
  newName.value = name.value || '';
  newComment.value = comment.value || '';
  emit('cancel');
};
</script>

<template>
  <v-form ref="form" v-model="isValid" @submit.prevent="submit">
    <v-card-text>
      <v-row dense>
        <v-col>
          <v-text-field
            v-model.trim="newName"
            :rules="[requiredRule]"
            variant="filled"
            maxlength="30"
            :label="t('bookmark_form_name')"
            :aria-label="t('bookmark_form_name')"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <v-textarea
            v-model.trim="newComment"
            variant="filled"
            maxlength="100"
            :hide-details="'auto'"
            :label="t('bookmark_form_comment')"
            :aria-label="t('bookmark_form_comment')"
          />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="d-flex justify-space-between mb-2">
      <v-btn
        v-if="state === 'edit'"
        :text="t('button_delete')"
        :loading="isRemoving"
        color="error"
        variant="flat"
        @click="emit('remove')"
      />
      <v-spacer></v-spacer>
      <v-btn :text="t('button_cancel')" color="primary" variant="outlined" @click="handleCancel" />
      <v-btn
        :text="t('button_save')"
        :loading="isSaving"
        :disabled="!isValid"
        color="primary"
        variant="flat"
        type="submit"
      />
    </v-card-actions>
  </v-form>
</template>

<style scoped lang="scss"></style>
